<template>
  <section class="blog-comments" :aria-label="`Comentários da matéria ${postId}`">
    <div v-if="loading && !comments.length" class="blog-comments__loading">
      <v-progress-circular indeterminate size="24" color="#ffcf5a" /> Carregando comentários…
    </div>
    <v-alert v-else-if="error && !comments.length" dense text type="error">
      {{ error }}
      <button type="button" class="blog-comments__retry" @click="load()">Tentar novamente</button>
    </v-alert>
    <div v-else-if="!comments.length" class="blog-comments__empty">Seja a primeira pessoa a comentar.</div>

    <article v-for="comment in comments" :key="comment.id" class="blog-comment">
      <v-avatar size="32" color="#302737">
        <v-img v-if="comment.author.profile_photo" :src="mediaUrl(comment.author.profile_photo)" :alt="`Foto de ${comment.author.nickname || 'pessoa da comunidade'}`" />
        <v-icon v-else size="18">mdi-account</v-icon>
      </v-avatar>
      <div class="blog-comment__body">
        <header>
          <strong>{{ comment.author.nickname || "Pessoa da comunidade" }}</strong>
          <time :datetime="comment.created_at">{{ relativeDate(comment.created_at) }}</time>
        </header>
        <template v-if="editingId === comment.id">
          <v-textarea v-model="editContent" outlined dense auto-grow rows="2" maxlength="1000" hide-details />
          <div class="blog-comment__edit-actions">
            <v-btn x-small text @click="cancelEdit">Cancelar</v-btn>
            <v-btn x-small depressed color="#ffcf5a" :loading="pendingId === comment.id" @click="saveEdit(comment)">Salvar</v-btn>
          </div>
        </template>
        <p v-else>{{ comment.content }}</p>
      </div>
      <v-menu v-if="comment.can_edit || comment.can_delete" left offset-y>
        <template #activator="{ on, attrs }">
          <v-btn icon x-small aria-label="Ações do comentário" v-bind="attrs" v-on="on"><v-icon size="18">mdi-dots-horizontal</v-icon></v-btn>
        </template>
        <v-list dense dark>
          <v-list-item v-if="comment.can_edit" @click="startEdit(comment)"><v-list-item-title>Editar</v-list-item-title></v-list-item>
          <v-list-item v-if="comment.can_delete" @click="remove(comment)"><v-list-item-title class="error--text">Excluir</v-list-item-title></v-list-item>
        </v-list>
      </v-menu>
    </article>

    <v-btn v-if="hasMore" text small :loading="loadingMore" @click="loadMore">Carregar mais comentários</v-btn>

    <form class="blog-comments__form" @submit.prevent="submit">
      <v-textarea
        v-model="content"
        outlined
        dense
        auto-grow
        rows="1"
        maxlength="1000"
        hide-details
        placeholder="Escreva um comentário…"
        :disabled="submitting"
        :aria-label="authenticated ? 'Escreva um comentário' : 'Faça login para comentar'"
      />
      <v-btn icon type="submit" color="#ffcf5a" :loading="submitting" :disabled="!content.trim()" aria-label="Enviar comentário">
        <v-icon>mdi-send</v-icon>
      </v-btn>
    </form>
  </section>
</template>

<script>
import { createBlogComment, deleteBlogComment, getBlogComments, updateBlogComment, blogErrorMessage } from "@/server/BlogService";
import { socialMediaUrl } from "@/server/SocialService";

export default {
  name: "BlogComments",
  props: {
    postId: { type: Number, required: true },
    authenticated: { type: Boolean, default: false },
  },
  data: () => ({
    comments: [],
    cursor: null,
    hasMore: false,
    loading: false,
    loadingMore: false,
    submitting: false,
    content: "",
    error: "",
    editingId: null,
    editContent: "",
    pendingId: null,
  }),
  mounted() {
    this.load();
  },
  methods: {
    mediaUrl: socialMediaUrl,
    relativeDate(date) {
      const value = new Date(date);
      return Number.isNaN(value.getTime())
        ? ""
        : new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }).format(value);
    },
    merge(items) {
      const map = new Map(this.comments.map((item) => [item.id, item]));
      items.forEach((item) => map.set(item.id, item));
      this.comments = Array.from(map.values());
    },
    async load(cursor = null) {
      const more = !!cursor;
      this[more ? "loadingMore" : "loading"] = true;
      this.error = "";
      try {
        const body = await getBlogComments(this.postId, cursor);
        const page = body.data;
        this.merge(page.items || []);
        this.cursor = page.next_cursor;
        this.hasMore = page.has_more;
      } catch (error) {
        this.error = blogErrorMessage(error, "Não foi possível carregar os comentários.");
      } finally {
        this[more ? "loadingMore" : "loading"] = false;
      }
    },
    loadMore() {
      if (this.cursor) this.load(this.cursor);
    },
    requireAuth() {
      if (this.authenticated) return true;
      this.$router.push({ path: "/login", query: { redirect: this.$route.fullPath } });
      return false;
    },
    async submit() {
      if (!this.content.trim() || !this.requireAuth()) return;
      this.submitting = true;
      try {
        const body = await createBlogComment(this.postId, this.content.trim());
        this.comments.push(body.data.comment);
        this.content = "";
        this.$emit("count", body.data.comments_count);
      } catch (error) {
        this.$emit("error", blogErrorMessage(error, "Não foi possível publicar o comentário."));
      } finally {
        this.submitting = false;
      }
    },
    startEdit(comment) {
      this.editingId = comment.id;
      this.editContent = comment.content;
    },
    cancelEdit() {
      this.editingId = null;
      this.editContent = "";
    },
    async saveEdit(comment) {
      if (!this.editContent.trim()) return;
      this.pendingId = comment.id;
      try {
        const body = await updateBlogComment(comment.id, this.editContent.trim());
        const index = this.comments.findIndex((item) => item.id === comment.id);
        this.$set(this.comments, index, body.data.comment);
        this.cancelEdit();
      } catch (error) {
        this.$emit("error", blogErrorMessage(error, "Não foi possível salvar o comentário."));
      } finally {
        this.pendingId = null;
      }
    },
    async remove(comment) {
      this.pendingId = comment.id;
      try {
        await deleteBlogComment(comment.id);
        this.comments = this.comments.filter((item) => item.id !== comment.id);
        this.$emit("count-delta", -1);
      } catch (error) {
        this.$emit("error", blogErrorMessage(error, "Não foi possível excluir o comentário."));
      } finally {
        this.pendingId = null;
      }
    },
  },
};
</script>